// client/src/components/bankAccounts/BankAccountForm.js
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  Paper,
  Box,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress
} from '@mui/material';
import { Save as SaveIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { LocaleContext } from '../../contexts/LocaleContext';
import bankAccountService from '../../services/bankAccountService';

const BankAccountForm = () => {
  const { t } = useTranslation();
  const { direction } = useContext(LocaleContext);
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState({
    name: '',
    account_number: '',
    reference_no1: '',
    reference_no2: ''
  });
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(isEditMode);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Load bank account data if in edit mode
  useEffect(() => {
    if (isEditMode) {
      const loadBankAccount = async () => {
        try {
          setFetchLoading(true);
          const response = await bankAccountService.getBankAccount(id);
          setFormData(response.data);
        } catch (err) {
          setError(t('errorLoadingBankAccount'));
          navigate('/bank-accounts');
        } finally {
          setFetchLoading(false);
        }
      };
      
      loadBankAccount();
    }
  }, [isEditMode, id, navigate, t]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      if (isEditMode) {
        await bankAccountService.updateBankAccount(id, formData);
        setSuccess(t('bankAccountUpdatedSuccess'));
      } else {
        await bankAccountService.createBankAccount(formData);
        setSuccess(t('bankAccountCreatedSuccess'));
        setFormData({
          name: '',
          account_number: '',
          reference_no1: '',
          reference_no2: ''
        });
      }
    } catch (err) {
      setError(isEditMode ? t('errorUpdatingBankAccount') : t('errorCreatingBankAccount'));
    } finally {
      setLoading(false);
      window.scrollTo(0, 0);
    }
  };

  if (fetchLoading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          {isEditMode ? t('editBankAccount') : t('addBankAccount')}
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
        
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                id="name"
                name="name"
                label={t('accountName')}
                value={formData.name}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                id="account_number"
                name="account_number"
                label={t('accountNumber')}
                value={formData.account_number}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="reference_no1"
                name="reference_no1"
                label={t('referenceNo1')}
                value={formData.reference_no1}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="reference_no2"
                name="reference_no2"
                label={t('referenceNo2')}
                value={formData.reference_no2}
                onChange={handleChange}
              />
            </Grid>
          </Grid>
          
          <Box sx={{ 
            mt: 3, 
            display: 'flex',
            gap: 2,
            justifyContent: direction === 'ltr' ? 'flex-end' : 'flex-start'
          }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/bank-accounts')}
            >
              {t('cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={loading}
            >
              {loading ? t('saving') : t('save')}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default BankAccountForm;